import type { Direction, SaveData, TileType } from "./domain/types";
import type { GameEngine, MoveResult } from "./game/GameEngine";
import { tutorialDefinition } from "./game/LevelSystem";
import { RunSession } from "./app/RunSession";
import { createAnalytics } from "./infrastructure/analytics/createAnalytics";
import { SaveSystem } from "./infrastructure/storage/SaveSystem";
import { BrowserAudioAdapter } from "./infrastructure/audio/Audio";
import { GridRenderer } from "./ui/renderGrid";
import { MobilePlatform } from "./infrastructure/mobile/MobilePlatform";
import { dailyChallengeSeed, dateKey } from "./domain/DailyChallenge";
import { calculateRunReward } from "./domain/RewardService";
import { BrowserAdSenseAdapter, readAdSenseConfig } from "./infrastructure/ads/AdSense";
import { animateNumber } from "./ui/animateNumber";
import { relics } from "./domain/RelicService";
import { swipeDirection } from "./ui/SwipeInput";
import { canClaimDailyReward, claimDailyReward } from "./domain/DailyReward";
import { explorerJournal } from "./domain/ExplorerJournal";
import { explorerLevel as calculateExplorerLevel, isLevelUnlocked, xpForLevel } from "./domain/VerticalSlice";
import { achievements as achievementCatalog, type AchievementDefinition } from "./domain/AchievementService";
import { createErrorReporter, installGlobalErrorHandlers } from "./infrastructure/monitoring/createErrorReporter";

// Must match this game's id in vibwebs-site's lib/games.ts exactly.
const GAME_ID = "treasure-grid";

const worlds = [
  [101, "Shadow Grid", "shadow"], [81, "Sky Temples", "sky"], [61, "Frozen Vaults", "frozen"],
  [41, "Desert Tombs", "desert"], [21, "Crystal Caverns", "crystal"], [1, "Forgotten Ruins", "ruins"],
] as const;

function currentWorld(): string {
  return worlds.find(([from]) => save.progression.campaignLevel >= from)?.[1] ?? "Forgotten Ruins";
}

function currentWorldSlug(): string {
  return worlds.find(([from]) => save.progression.campaignLevel >= from)?.[2] ?? "ruins";
}

function applyWorldTheme(): void {
  document.querySelector<HTMLElement>(".app")!.dataset.world = currentWorldSlug();
}

const byId = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
};

const saveSystem = new SaveSystem();
let save: SaveData = saveSystem.load();
const analytics = createAnalytics(save.playerId, import.meta.env);
installGlobalErrorHandlers(createErrorReporter(import.meta.env));
const session = new RunSession(save, saveSystem);
const audio = new BrowserAudioAdapter();
const mobile = new MobilePlatform();
const ads = new BrowserAdSenseAdapter(readAdSenseConfig(import.meta.env));
let engine: GameEngine | undefined;
let currentSeed = 0;
let tutorialActive = !save.settings.tutorialCompleted;
let tutorialHasMoved = false;
let gridRenderer: GridRenderer;
let timerHandle: number | undefined;
let dailyMode = false;
let lastReportedCombo = 1;
let displayedScore = 0;
let displayedGold = save.economy.gold;
let armedRisk: Direction | undefined;
let feedbackId = 0;
let damageFeedbackId = 0;
let lastRunWon = false;
let lastShareSummary = "";

interface TreasureGridQaHooks {
  completeRun(won?: boolean): void;
  readyToBank(): void;
  save(): SaveData;
}

declare global {
  interface Window {
    __treasureGridQa?: TreasureGridQaHooks;
  }
}

function explorerLevel(): number {
  return calculateExplorerLevel(save.progression.xp);
}
function renderMeta(): void {
  const explorer = explorerLevel();
  const current = xpForLevel(explorer);
  const next = xpForLevel(explorer + 1);
  byId("profile-level").textContent = String(explorer);
  byId("xp-fill").style.width = `${((save.progression.xp - current) / Math.max(1, next - current)) * 100}%`;
  byId("xp-copy").textContent = `${save.progression.xp - current} / ${next - current} XP`;
  byId("level").textContent = String(save.progression.campaignLevel);
  animateNumber(byId("gold"), displayedGold, save.economy.gold, { reducedMotion: save.settings.reducedMotion });
  displayedGold = save.economy.gold;
  byId("gems").textContent = `${save.economy.gems} gems`;
  byId("relics").textContent = String(save.economy.relics);
  byId("relic-fragments").textContent = `${save.relicFragments} / 5`;
  byId("hud-gold").textContent = save.economy.gold.toLocaleString();
  byId("hud-fragments").textContent = `${save.relicFragments}/5`;
  const world = worlds.find(([from]) => save.progression.campaignLevel >= from)![1];
  byId("world-name").textContent = world;
  applyWorldTheme();
  byId("route-label").textContent = dailyMode
    ? "Daily expedition"
    : `${world} - Level ${save.progression.campaignLevel}`;
}

function startRun(): void {
  const level = save.progression.campaignLevel;
  const world = currentWorld();
  currentSeed = dailyMode ? dailyChallengeSeed() : (Date.now() ^ (level * 2654435761)) >>> 0;
  session.dailyMode = dailyMode;
  engine = session.start(currentSeed);
  ads.hideBanner(byId("web-ad-banner"));
  tutorialHasMoved = false;
  lastReportedCombo = 1;
  byId("seed-label").textContent = `Seed ${currentSeed.toString(16).toUpperCase()}`;
  byId("new-run").textContent = "Restart run";
  const tutorial = tutorialDefinition(level);
  setStatus(tutorial?.hint ?? "Find treasure to unlock the exit.");
  analytics.track("level_started", { level, seed: currentSeed, levelId: engine.state.levelId, world });
  analytics.track("run_started", { levelId: engine.state.levelId, runId: String(currentSeed), world });
  if (dailyMode) analytics.track("daily_challenge_started", { runId: String(currentSeed) });
  if (tutorialActive) analytics.track("tutorial_started", { levelId: engine.state.levelId });
  void audio.resume();
  audio.startMusic();
  audio.playEffect("ui");
  render();
  startTimer();
}

function move(direction: Direction): void {
  if (!engine) return;
  if (engine.isRisky(direction) && armedRisk !== direction) {
    armedRisk = direction;
    setStatus(`${engine.inspect(direction)} - move again to enter.`);
    return;
  }
  armedRisk = undefined;
  const before = engine.state.playerIndex;
  const offset = { up: -engine.state.size, down: engine.state.size, left: -1, right: 1 }[direction];
  const enteredIndex = before + offset;
  const wasUnknown = engine.state.grid[enteredIndex]?.discoveryState === "unknown";
  const result = session.move(direction);
  if (!result.valid) { setStatus(result.message); return; }
  if (wasUnknown && engine.state.playerIndex === enteredIndex) {
    analytics.track("tile_revealed", {
      levelId: engine.state.levelId,
      runId: String(engine.state.seed),
      tileIndex: enteredIndex,
    });
  }
  tutorialHasMoved ||= engine.state.playerIndex !== before;
  audio.playEffect(result.event === "treasure" ? "treasure" :
    result.event === "trap" || result.event === "curse" ? "trap" :
    result.event === "secretRoom" ? "win" :
    result.event === "win" ? "win" :
    result.event === "loss" ? "loss" : "move");
  if (result.event === "win") void mobile.success(save.settings.hapticsEnabled);
  else if (result.event === "trap" || result.event === "loss") void mobile.error(save.settings.hapticsEnabled);
  else void mobile.impact(save.settings.hapticsEnabled);
  handleResult(result);
  if (engine.state.combo.best > lastReportedCombo) {
    lastReportedCombo = engine.state.combo.best;
    analytics.track("combo_reached", {
      levelId: engine.state.levelId, runId: String(engine.state.seed), combo: lastReportedCombo,
    });
    analytics.track("combo_milestone_reached", {
      levelId: engine.state.levelId, runId: String(engine.state.seed), combo: lastReportedCombo,
    });
    if (lastReportedCombo >= 2) showComboMilestone(lastReportedCombo);
  }
  render();
}

function inspect(direction: Direction): void {
  if (!engine) return;
  const message = engine.inspect(direction);
  if (message !== "The chamber is silent") setStatus(message);
}

function handleResult(result: MoveResult): void {
  if (!engine) return;
  setStatus(result.message, result.event === "trap" || result.event === "loss");
  if (result.event === "trap") {
    analytics.track("trap_triggered", { hp: engine.state.hp });
    pulseBody("trap-shake");
    if (engine.state.lastDamageFeedback) damageFeedbackId++;
  }
  if (result.event === "curse") {
    analytics.track("curse_activated", {
      levelId: engine.state.levelId,
      runId: String(engine.state.seed),
      curse: engine.state.activeCurse?.type ?? "unknown",
    });
    analytics.track("curse_triggered", {
      levelId: engine.state.levelId,
      runId: String(engine.state.seed),
      curse: engine.state.activeCurse?.type ?? "unknown",
    });
    const status = byId("status");
    status.classList.remove("curse-alert");
    void status.offsetWidth;
    status.classList.add("curse-alert");
  }
  if (result.event === "secretRoom" && engine.state.lastSecretRoom) {
    const room = engine.state.lastSecretRoom;
    const copies = {
      gold_chest: `Gold chest found: +${room.amount} Gold`,
      relic_fragment: `Relic fragment recovered: +${room.amount}`,
      combo_boost: `Ancient momentum: Combo +${room.amount}`,
    };
    byId("secret-room-copy").textContent = copies[room.reward];
    byId<HTMLDialogElement>("secret-room-dialog").showModal();
    analytics.track("secret_room_discovered", {
      levelId: engine.state.levelId,
      runId: String(engine.state.seed),
      reward: room.reward,
      amount: room.amount,
    });
    if (room.reward === "relic_fragment") {
      analytics.track("relic_fragment_collected", {
        levelId: engine.state.levelId,
        runId: String(engine.state.seed),
        fragmentsFound: engine.state.rewards.relicFragments,
        source: "secret_room",
      });
      showFragmentModal();
    }
  }
  if (result.event === "win" || result.event === "loss") finish(result.event === "win");
  if (result.event === "treasure" && engine.state.lastTreasureFeedback) {
    feedbackId++;
    const feedback = engine.state.lastTreasureFeedback;
    analytics.track("treasure_collected", {
      levelId: engine.state.levelId,
      runId: String(engine.state.seed),
      rewardType: feedback.rewardType,
      score: feedback.score,
      gold: feedback.gold,
      combo: feedback.combo,
    });
    analytics.track("treasure_found", {
      levelId: engine.state.levelId,
      runId: String(engine.state.seed),
      rewardType: feedback.rewardType,
      score: feedback.score,
      gold: feedback.gold,
    });
    if (feedback.rewardType === "relicFragment") {
      analytics.track("relic_fragment_collected", {
        levelId: engine.state.levelId,
        runId: String(engine.state.seed),
        fragmentsFound: engine.state.rewards.relicFragments,
      });
      showFragmentModal();
    }
  }
}

function pulseBody(className: string): void {
  document.body.classList.remove(className);
  void document.body.offsetWidth;
  document.body.classList.add(className);
  window.setTimeout(() => document.body.classList.remove(className), 500);
}

function showComboMilestone(combo: number): void {
  const banner = byId("combo-banner");
  banner.textContent = `COMBO x${combo}`;
  banner.hidden = false;
  banner.classList.add("combo-pop");
  window.setTimeout(() => { banner.hidden = true; banner.classList.remove("combo-pop"); }, 1100);
}

function showFragmentModal(): void {
  if (!engine) return;
  byId("fragment-modal-copy").textContent =
    `${save.relicFragments + engine.state.rewards.relicFragments}/5 fragments gathered toward the next relic.`;
  const dialog = byId<HTMLDialogElement>("relic-fragment-dialog");
  if (!dialog.open) dialog.showModal();
  pulseBody("relic-flash");
}

function finish(won: boolean): void {
  if (!engine) return;
  const state = engine.state;
  lastRunWon = won;
  const reward = calculateRunReward(state, Math.max(1, save.progression.campaignLevel - (won ? 1 : 0)), won);
  if (won && tutorialActive) completeTutorial();
  const duration = Math.max(0, state.timer.initialSeconds - state.timer.remainingSeconds);
  const world = currentWorld();
  analytics.track(won ? "level_completed" : "level_failed", {
    levelId: state.levelId,
    score: state.score,
    duration,
    world,
  });

  if (won && (window as any).GameLeaderboard) {
    (window as any).GameLeaderboard.recordScore(GAME_ID, { score: state.score, level: save.progression.campaignLevel });
  }
  analytics.track(won ? "expedition_completed" : "expedition_failed", {
    levelId: state.levelId,
    runId: String(state.seed),
    score: state.score,
    gold: reward.gold,
    bestCombo: state.combo.best,
    relicFragments: state.rewards.relicFragments,
  });
  analytics.track("run_ended", { levelId: state.levelId, runId: String(state.seed), won, duration, world });
  analytics.track("reward_claimed", {
    levelId: state.levelId, runId: String(state.seed),
    gold: reward.gold, xp: reward.xp, masteryPoints: reward.masteryPoints,
  });
  if (dailyMode && won) {
    analytics.track("daily_challenge_completed", { runId: String(state.seed), score: state.score });
  }
  stopTimer();

  const objectiveComplete = state.treasuresCollected >= state.treasureGoal;
  const failureReason = state.hp <= 0
    ? "Your HP reached zero after a dangerous chamber."
    : state.movesRemaining <= 0
      ? "You ran out of moves before escaping."
      : "The expedition ended before the relic could be secured.";
  const nextWorld = worlds.find(([from]) => save.progression.campaignLevel >= from)![1];
  const winReason = objectiveComplete
    ? `Objective complete: treasure recovered and the exit reached. Level ${save.progression.campaignLevel} is now ready in ${nextWorld}.`
    : `Exit reached with the expedition objective secured. Level ${save.progression.campaignLevel} is now ready in ${nextWorld}.`;

  byId("result-kicker").textContent = won ? "Expedition cleared" : "Expedition failed";
  byId("result-title").textContent = won ? "Relic secured." : "The ruins pushed back.";
  byId("result-copy").textContent = won ? winReason : failureReason;
  byId("result-rewards").innerHTML =
    `<div class="result-outcome ${won ? "success" : "failed"}"><b>${won ? "Cleared" : "Failed"}</b><span>${won ? "Level result" : "Try again"}</span></div>
     <div><b>+${reward.gold}</b><span>Gold earned</span></div>
     <div><b>${state.score}</b><span>Score</span></div>
     <div><b>x${state.combo.best}</b><span>Best combo</span></div>
     <div><b>+${state.rewards.relicFragments}</b><span>Relic fragments</span></div>
     <div><b>${Math.max(0, state.movesRemaining)}</b><span>Moves remaining</span></div>`;
  const starIcons = "★".repeat(session.lastStars) + "☆".repeat(Math.max(0, 3 - session.lastStars));
  byId("result-rewards").innerHTML +=
    `<div class="star-result"><b class="star-rating">${starIcons}</b><span>Level rating (${session.lastStars}/3)</span></div>
     ${session.lastMilestoneFragments ? `<div><b>+${session.lastMilestoneFragments} fragments</b><span>Milestone reward</span></div>` : ""}`;
  byId<HTMLButtonElement>("continue").textContent = won ? "Next run" : "Return to menu";
  byId<HTMLDialogElement>("result").showModal();
  if (session.lastUnlockedRelic) {
    byId("result-copy").textContent += ` Relic unlocked: ${session.lastUnlockedRelic.name}.`;
    analytics.track("relic_unlocked", {
      levelId: state.levelId,
      runId: String(state.seed),
      relicId: session.lastUnlockedRelic.id,
      relicName: session.lastUnlockedRelic.name,
    });
  }
  lastShareSummary = won
    ? `I just cleared ${world} - Level ${save.progression.campaignLevel - 1} in Treasure Expedition with a score of ${state.score.toLocaleString()} and a x${state.combo.best} combo!`
    : `I scored ${state.score.toLocaleString()} exploring ${world} in Treasure Expedition. Can you beat it?`;
  byId<HTMLButtonElement>("share-result").hidden = !canShare();
  ads.displayBanner(byId("web-ad-banner"));
  renderMeta();
  showAchievementToast(session.newlyUnlockedAchievements);
}

function goToGames(): void {
  window.location.href = "/games";
}

function canShare(): boolean {
  return typeof navigator !== "undefined" && (Boolean(navigator.share) || Boolean(navigator.clipboard));
}

async function shareResult(): Promise<void> {
  if (!lastShareSummary) return;
  const shareData = { title: "Treasure Expedition", text: lastShareSummary, url: window.location.href };
  const result = await window.GameShare.share(shareData);
  if (result === "shared") {
    analytics.track("result_shared", { method: "native" });
    return;
  }
  if (result === "cancelled") {
    // Treasure Grid's own product policy (predates this integration): even
    // a cancelled native share still attempts a clipboard copy.
    // GameShare.share() stops at "cancelled" for callers that want that
    // distinction (e.g. RuneVault); copy() explicitly requests the
    // clipboard fallback anyway.
    const copyResult = await window.GameShare.copy(shareData);
    if (copyResult === "copied") {
      analytics.track("result_shared", { method: "clipboard" });
      setStatus("Result copied to clipboard.");
    }
    return;
  }
  if (result === "copied") {
    analytics.track("result_shared", { method: "clipboard" });
    setStatus("Result copied to clipboard.");
  }
  // "unavailable": both native share and clipboard failed/unavailable -
  // sharing is best-effort, silently ignore, matching existing behavior.
}

function showAchievementToast(unlocked: AchievementDefinition[]): void {
  if (!unlocked.length) return;
  const toast = byId("achievement-toast");
  toast.innerHTML = unlocked.map(achievement =>
    `<div class="achievement-toast-item"><b>Achievement unlocked</b><span>${achievement.name}</span></div>`,
  ).join("");
  toast.hidden = false;
  toast.classList.remove("achievement-toast-pop");
  void toast.offsetWidth;
  toast.classList.add("achievement-toast-pop");
  window.setTimeout(() => {
    toast.hidden = true;
    toast.classList.remove("achievement-toast-pop");
  }, 1400 + unlocked.length * 1200);
  for (const achievement of unlocked) {
    analytics.track("achievement_unlocked", { achievementId: achievement.id, achievementName: achievement.name });
  }
}

function installQaHooks(): void {
  if (new URLSearchParams(window.location.search).get("qa") !== "1") return;
  window.__treasureGridQa = {
    completeRun: (won = true) => {
      if (!engine) startRun();
      if (!engine) throw new Error("No active run");
      engine.state.treasuresCollected = Math.max(engine.state.treasuresCollected, engine.state.treasureGoal);
      engine.state.rewards.gold = Math.max(engine.state.rewards.gold, 80);
      engine.state.score = Math.max(engine.state.score, 300);
      engine.state.combo.best = Math.max(engine.state.combo.best, 2);
      session.persistResult(won);
      finish(won);
      render();
    },
    readyToBank: () => {
      if (!engine) startRun();
      if (!engine) throw new Error("No active run");
      engine.state.treasuresCollected = engine.state.treasureGoal;
      engine.state.rewards.relicFragments = Math.max(
        engine.state.rewards.relicFragments,
        engine.state.objective.relicFragmentsRequired,
      );
      engine.state.lastSecretRoom = engine.state.objective.secretRoomsRequired > 0
        ? { reward: "gold_chest", amount: 1 }
        : engine.state.lastSecretRoom;
      render();
    },
    save: () => saveSystem.load(),
  };
}

function render(): void {
  if (!engine) return;
  const state = engine.state;
  document.body.classList.toggle("run-active", state.status === "playing");
  gridRenderer.render(state, tutorialActive ? tutorialTarget() : undefined,
    save.settings.reducedMotion, save.settings.highContrast, feedbackId, damageFeedbackId);
  animateNumber(byId("score"), displayedScore, state.score, { reducedMotion: save.settings.reducedMotion });
  displayedScore = state.score;
  byId("health").textContent = String(Math.max(0, state.hp));
  byId("moves").textContent = String(Math.max(0, state.movesRemaining));
  byId("timer").textContent = formatTime(state.timer.remainingSeconds);
  byId("status-effect").textContent = state.statusEffect === "none" ? "None" : state.statusEffect;
  byId("combo").textContent = `x${state.combo.current}`;
  byId("hud-gold").textContent = (save.economy.gold + state.rewards.gold).toLocaleString();
  byId("hud-treasure").textContent = `${state.treasuresCollected}/${state.treasureGoal}`;
  byId("hud-fragments").textContent = `${save.relicFragments + state.rewards.relicFragments}/5`;
  const objectiveParts = [
    state.treasureGoal > 0 ? `${state.treasuresCollected}/${state.treasureGoal} treasure` : "",
    state.objective.relicFragmentsRequired > 0
      ? `${state.rewards.relicFragments}/${state.objective.relicFragmentsRequired} fragment` : "",
    state.objective.secretRoomsRequired > 0
      ? `${state.lastSecretRoom ? 1 : 0}/${state.objective.secretRoomsRequired} secret room` : "",
  ].filter(Boolean);
  const treasureObjective = state.treasureGoal > 0;
  const fragmentObjective = state.objective.relicFragmentsRequired > 0;
  const secretObjective = state.objective.secretRoomsRequired > 0;
  const objectiveCount = Number(treasureObjective) + Number(fragmentObjective) + Number(secretObjective);
  const objectiveDone =
    Number(treasureObjective && state.treasuresCollected >= state.treasureGoal) +
    Number(fragmentObjective && state.rewards.relicFragments >= state.objective.relicFragmentsRequired) +
    Number(secretObjective && (state.lastSecretRoom ? 1 : 0) >= state.objective.secretRoomsRequired);
  byId("objective").textContent = state.objective.secretRoomsRequired ? "Discover & escape"
    : state.objective.relicFragmentsRequired ? "Recover the fragment" : `Recover ${state.treasureGoal} treasure`;
  byId("objective-copy").textContent = `${objectiveParts.join(" - ")} - Reach exit`;
  byId("objective-fill").style.width = `${objectiveCount ? objectiveDone / objectiveCount * 100 : 100}%`;
  const potCount = getItemQuantity("health_potion");
  const compCount = getItemQuantity("dowsing_compass");
  const potBtn = document.getElementById("use-health-potion") as HTMLButtonElement | null;
  const compBtn = document.getElementById("use-compass") as HTMLButtonElement | null;
  const countPotEl = document.getElementById("count-potion");
  const countCompEl = document.getElementById("count-compass");
  if (countPotEl) countPotEl.textContent = String(potCount);
  if (countCompEl) countCompEl.textContent = String(compCount);
  if (potBtn) potBtn.disabled = potCount <= 0 || state.status !== "playing";
  if (compBtn) compBtn.disabled = compCount <= 0 || state.status !== "playing";
  renderDecisionPanel();
  renderTutorialCoach();
}

function renderDecisionPanel(): void {
  if (!engine) return;
  const state = engine.state;
  const objectiveComplete = state.treasuresCollected >= state.treasureGoal
    && state.rewards.relicFragments >= state.objective.relicFragmentsRequired
    && (state.objective.secretRoomsRequired === 0 || Boolean(state.lastSecretRoom));
  const neighbors = adjacentIndexes(state.playerIndex, state.size)
    .filter(index => state.grid[index]?.walkable);
  const dangerNeighbors = neighbors.filter(index => {
    const tile = state.grid[index];
    return tile.omen === "cursed_chamber" || ["trap", "poison", "curse"].includes(tile.type);
  }).length;
  const safeNeighbors = neighbors.length - dangerNeighbors;
  const haul = state.rewards.gold + Math.floor(state.score / 20);
  const lowHp = state.hp <= 35;
  const lowTime = state.timer.remainingSeconds <= 20;
  const lowMoves = state.movesRemaining <= 5;
  const shouldExit = objectiveComplete && (lowHp || lowTime || lowMoves || haul >= 80);
  const bankReward = calculateRunReward(state, Math.max(1, save.progression.campaignLevel), true);
  const treasureRemaining = Math.max(0, state.treasureGoal - state.treasuresCollected);

  byId("decision-title").textContent = objectiveComplete
    ? shouldExit ? "Bank your haul now" : "Exit ready - push carefully"
    : "Unlock the exit";
  byId("decision-copy").textContent = objectiveComplete
    ? `Enter the exit now to bank +${bankReward.gold} gold and +${bankReward.xp} XP. Keep exploring only if the extra haul is worth the risk.`
    : `Recover ${treasureRemaining} more treasure to unlock the exit. Extra reveals can grow your haul, but traps cost HP, moves, and time.`;
  byId("decision-exit").textContent = objectiveComplete ? "Exit ready" : "Exit locked";
  byId("decision-risk").textContent = dangerNeighbors
    ? `${dangerNeighbors} danger nearby`
    : safeNeighbors ? "Safe route near" : "No safe route";
  byId("decision-haul").textContent = `Carry ${haul}`;
  byId("decision-bank").textContent = objectiveComplete
    ? `Bank +${bankReward.gold}`
    : treasureRemaining ? `Need ${treasureRemaining} treasure` : "Find the exit";
  byId("decision-panel").classList.toggle("decision-escape", objectiveComplete);
  byId("decision-panel").classList.toggle("decision-danger", lowHp || lowTime || lowMoves || dangerNeighbors >= 2);
}

function adjacentIndexes(index: number, size: number): number[] {
  const row = Math.floor(index / size);
  const column = index % size;
  return [[row - 1, column], [row + 1, column], [row, column - 1], [row, column + 1]]
    .filter(([nextRow, nextColumn]) => nextRow >= 0 && nextRow < size && nextColumn >= 0 && nextColumn < size)
    .map(([nextRow, nextColumn]) => nextRow * size + nextColumn);
}

function tutorialTarget(): number | undefined {
  if (!engine || !tutorialActive) return undefined;
  const state = engine.state;
  const desired = !tutorialHasMoved
    ? (_type: TileType, index: number) => index !== state.playerIndex
    : state.treasuresCollected < state.treasureGoal
      ? (type: TileType) => ["coin", "treasure", "gem", "relic"].includes(type)
      : (type: TileType) => type === "exit";
  const queue = [state.playerIndex];
  const firstStep = new Map<number, number>();
  const seen = new Set(queue);
  while (queue.length) {
    const current = queue.shift()!;
    if (current !== state.playerIndex && desired(state.grid[current].type, current)) {
      return firstStep.get(current) ?? current;
    }
    const row = Math.floor(current / state.size), column = current % state.size;
    for (const [dy, dx] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const y = row + dy, x = column + dx;
      if (y < 0 || y >= state.size || x < 0 || x >= state.size) continue;
      const next = y * state.size + x;
      if (!seen.has(next) && state.grid[next].walkable) {
        seen.add(next);
        firstStep.set(next, current === state.playerIndex ? next : firstStep.get(current)!);
        queue.push(next);
      }
    }
  }
  return undefined;
}

function renderTutorialCoach(): void {
  const coach = byId<HTMLElement>("tutorial-coach");
  if (!tutorialActive || !engine) { coach.hidden = true; return; }
  coach.hidden = false;
  const state = engine.state;
  let step = 1, title = "Move through the ruins", copy = "Use WASD, arrow keys, the direction pad, or tap the glowing neighboring tile.";
  if (tutorialHasMoved) {
    step = 2; title = "Recover treasure"; copy = "Follow the glowing route. Coins and chests unlock the exit.";
  }
  if (state.treasuresCollected > 0 && state.treasuresCollected < state.treasureGoal) {
    step = 3; title = "Complete the objective"; copy = `Find ${state.treasureGoal - state.treasuresCollected} more treasure before your moves run out.`;
  }
  if (state.treasuresCollected >= state.treasureGoal) {
    step = 4; title = "Escape the grid"; copy = "The vault exit is unlocked. Follow the glowing route and enter it.";
  }
  byId("coach-step").textContent = `Step ${step} of 4`;
  byId("coach-title").textContent = title;
  byId("coach-copy").textContent = copy;
}

function completeTutorial(): void {
  tutorialActive = false;
  save.settings.tutorialCompleted = true;
  saveSystem.save(save);
  analytics.track("tutorial_completed", { levelId: engine?.state.levelId ?? "tutorial" });
  render();
}

function setStatus(message: string, danger = false): void {
  const status = byId("status");
  status.textContent = message;
  status.style.color = danger ? "var(--red)" : "";
  byId("discovery").innerHTML = `<b>${danger ? "Danger" : "Expedition log"}</b><span>${message}</span>`;
}

function pauseGame(): void {
  if (!engine?.pause()) return;
  analytics.track("game_paused", {
    levelId: engine.state.levelId,
    world: currentWorld(),
    duration: Math.max(0, engine.state.timer.initialSeconds - engine.state.timer.remainingSeconds),
  });
  document.body.classList.add("is-paused");
  byId<HTMLDialogElement>("pause-dialog").showModal();
  audio.playEffect("ui");
  gridRenderer.setPaused(true);
  render();
  stopTimer();
}

function pauseFromLifecycle(): void {
  if (engine?.state.status === "playing") pauseGame();
}

function handleNativeBack(): void {
  const settingsDialog = byId<HTMLDialogElement>("settings-dialog");
  const guideDialog = byId<HTMLDialogElement>("guide");
  const resultDialog = byId<HTMLDialogElement>("result");
  if (settingsDialog.open) settingsDialog.close();
  else if (guideDialog.open) guideDialog.close();
  else if (resultDialog.open) resultDialog.close();
  else if (engine?.state.status === "paused") resumeGame();
  else pauseGame();
}

function resumeGame(): void {
  if (!engine?.resume()) return;
  analytics.track("game_resumed", {
    levelId: engine.state.levelId,
    world: currentWorld(),
    duration: Math.max(0, engine.state.timer.initialSeconds - engine.state.timer.remainingSeconds),
  });
  document.body.classList.remove("is-paused");
  byId<HTMLDialogElement>("pause-dialog").close();
  audio.playEffect("ui");
  gridRenderer.setPaused(false);
  render();
  startTimer();
}

// GamePrivacy.getState() already implements the exact same three-shape
// backward compatibility this used to do locally (legacy bare string,
// Diamond Rush's wrapped shape, the flat {advertising,analytics} shape) -
// see public/game-privacy.js.
function analyticsConsentGranted(): boolean {
  return window.GamePrivacy.getState().categories.analytics === "granted";
}

function syncAnalyticsConsentControl(): void {
  byId<HTMLInputElement>("analytics-consent").checked = analyticsConsentGranted();
}

function openPrivacyChoices(): void {
  if (byId<HTMLDialogElement>("pause-dialog").open) byId<HTMLDialogElement>("pause-dialog").close();
  syncAnalyticsConsentControl();
  byId<HTMLDialogElement>("privacy-choices-dialog").showModal();
}

function savePrivacyChoices(): void {
  const granted = byId<HTMLInputElement>("analytics-consent").checked;
  // Treasure Grid's dialog only exposes a single analytics toggle (no
  // ads here yet), so both shared categories mirror it - keeps the
  // stored shape cross-compatible with apps that track them separately.
  // GamePrivacy.save() persists under the shared vibwebs-analytics-consent
  // key and dispatches vibwebs:analytics-consent-change itself.
  const value = granted ? "granted" : "denied";
  window.GamePrivacy.save({ advertising: value, analytics: value });
  byId<HTMLDialogElement>("privacy-choices-dialog").close();
  if (engine?.state.status === "paused") byId<HTMLDialogElement>("pause-dialog").showModal();
}

function openSettings(): void {
  byId<HTMLInputElement>("music-volume").value = String(save.settings.musicVolume);
  byId<HTMLInputElement>("sfx-volume").value = String(save.settings.sfxVolume);
  byId<HTMLInputElement>("reduced-motion").checked = save.settings.reducedMotion;
  byId<HTMLInputElement>("high-contrast").checked = save.settings.highContrast;
  byId<HTMLInputElement>("color-blind-symbols").checked = save.settings.colorBlindSymbols;
  byId<HTMLInputElement>("haptics").checked = save.settings.hapticsEnabled;
  byId<HTMLSelectElement>("cosmetic-theme").value = save.settings.cosmeticTheme;
  if (byId<HTMLDialogElement>("pause-dialog").open) byId<HTMLDialogElement>("pause-dialog").close();
  byId<HTMLDialogElement>("settings-dialog").showModal();
}

function openMainMenu(): void {
  const hasSave = save.progression.runs > 0 || save.progression.campaignLevel > 1;
  byId("menu-best-score").textContent = save.progression.bestScore.toLocaleString();
  byId("menu-progress").textContent = hasSave
    ? `Forgotten Ruins - Level ${save.progression.campaignLevel} - ${save.economy.gold.toLocaleString()} gold`
    : "Your first expedition awaits";
  byId<HTMLButtonElement>("continue-expedition").hidden = !hasSave;
  const dialog = byId<HTMLDialogElement>("main-menu-dialog");
  if (!dialog.open) dialog.showModal();
}

function openDailyReward(): void {
  const claimable = canClaimDailyReward(save.daily, new Date());
  byId("daily-reward-copy").textContent = claimable
    ? `Day ${save.daily.streak % 7 + 1}: claim your temple tribute.`
    : "Today's tribute has already been claimed.";
  byId("streak-days").innerHTML = Array.from({ length: 7 }, (_, index) =>
    `<span class="${index < save.daily.streak ? "claimed" : ""}">${index + 1}${index === 6 ? " fragment" : ""}</span>`
  ).join("");
  byId<HTMLButtonElement>("claim-daily-reward").disabled = !claimable;
  byId<HTMLDialogElement>("daily-reward-dialog").showModal();
}

function claimReward(): void {
  if (!canClaimDailyReward(save.daily, new Date())) {
    byId("daily-reward-copy").textContent = "Today's tribute has already been claimed.";
    byId<HTMLButtonElement>("claim-daily-reward").disabled = true;
    return;
  }
  const result = claimDailyReward(save.daily, new Date());
  save.daily.lastRewardDate = result.state.lastRewardDate;
  save.daily.streak = result.state.streak;
  save.economy.gold += result.reward.gold;
  save.relicFragments += result.reward.relicFragments;
  saveSystem.save(save);
  analytics.track("daily_reward_claimed", { result: "claimed", rewardDay: result.reward.day });
  renderMeta();
  byId("daily-reward-copy").textContent =
    `Day ${result.reward.day}: +${result.reward.gold} gold${result.reward.relicFragments ? " and +1 fragment" : ""}.`;
  byId<HTMLButtonElement>("claim-daily-reward").disabled = true;
  byId("streak-days").innerHTML = Array.from({ length: 7 }, (_, index) =>
    `<span class="${index < save.daily.streak ? "claimed" : ""}">${index + 1}${index === 6 ? " fragment" : ""}</span>`
  ).join("");
}

function applySettings(): void {
  document.body.classList.toggle("reduced-motion", save.settings.reducedMotion);
  document.body.classList.toggle("high-contrast", save.settings.highContrast);
  document.body.dataset.theme = save.settings.cosmeticTheme;
  audio.setVolumes(save.settings.musicVolume, save.settings.sfxVolume);
}

function saveSettings(): void {
  save.settings.musicVolume = Number(byId<HTMLInputElement>("music-volume").value);
  save.settings.sfxVolume = Number(byId<HTMLInputElement>("sfx-volume").value);
  save.settings.reducedMotion = byId<HTMLInputElement>("reduced-motion").checked;
  save.settings.highContrast = byId<HTMLInputElement>("high-contrast").checked;
  save.settings.colorBlindSymbols = byId<HTMLInputElement>("color-blind-symbols").checked;
  save.settings.hapticsEnabled = byId<HTMLInputElement>("haptics").checked;
  save.settings.cosmeticTheme = byId<HTMLSelectElement>("cosmetic-theme").value as SaveData["settings"]["cosmeticTheme"];
  saveSystem.save(save);
  analytics.track("settings_changed", {
    reducedMotion: save.settings.reducedMotion,
    highContrast: save.settings.highContrast,
    colorBlindSymbols: save.settings.colorBlindSymbols,
    hapticsEnabled: save.settings.hapticsEnabled,
  });
  applySettings();
  byId<HTMLDialogElement>("settings-dialog").close();
  if (engine?.state.status === "paused") byId<HTMLDialogElement>("pause-dialog").showModal();
}

function resetSave(): void {
  if (!window.confirm("Reset all Treasure Expedition progress? This cannot be undone.")) return;
  stopTimer();
  save = saveSystem.reset();
  session.replaceSave(save);
  engine = undefined;
  tutorialActive = true;
  dailyMode = false;
  displayedScore = 0;
  displayedGold = 0;
  applySettings();
  renderMeta();
  byId<HTMLDialogElement>("settings-dialog").close();
  openMainMenu();
}

function getItemQuantity(itemId: string): number {
  return save.inventory.find(i => i.itemId === itemId)?.quantity ?? 0;
}

function changeItemQuantity(itemId: string, delta: number): void {
  let item = save.inventory.find(i => i.itemId === itemId);
  if (!item) {
    if (delta <= 0) return;
    item = { itemId, quantity: 0 };
    save.inventory.push(item);
  }
  item.quantity = Math.max(0, item.quantity + delta);
  saveSystem.save(save);
}

function useHealthPotion(): void {
  if (!engine || engine.state.status !== "playing") return;
  const count = getItemQuantity("health_potion");
  if (count <= 0) { setStatus("No Health Potions in pack."); return; }
  if (engine.state.hp >= 100) { setStatus("HP is already full."); return; }
  changeItemQuantity("health_potion", -1);
  engine.state.hp = Math.min(100, engine.state.hp + 30);
  audio.playEffect("ui");
  setStatus("Health Potion used: +30 HP restored!");
  render();
}

function useCompass(): void {
  if (!engine || engine.state.status !== "playing") return;
  const count = getItemQuantity("dowsing_compass");
  if (count <= 0) { setStatus("No Compasses in pack."); return; }
  const hiddenTiles = engine.state.grid
    .map((tile, idx) => ({ tile, idx }))
    .filter(item => item.tile.discoveryState === "unknown" && item.tile.type !== "empty");
  if (!hiddenTiles.length) { setStatus("No hidden tiles remain."); return; }
  changeItemQuantity("dowsing_compass", -1);
  const toReveal = hiddenTiles.slice(0, 2);
  toReveal.forEach(item => { item.tile.discoveryState = "revealed"; });
  audio.playEffect("ui");
  setStatus(`Compass used: ${toReveal.length} hidden tile(s) revealed!`);
  render();
}

function openScreen(name: string): void {
  ads.displayBanner(byId("web-ad-banner"));
  const title = byId("screen-title");
  const kicker = byId("screen-kicker");
  const content = byId("screen-content");
  kicker.textContent = "Explorer Guild";
  const worldCards = worlds.slice().reverse().map(([from, world]) => {
    const unlocked = save.progression.campaignLevel >= from;
    return `<article class="screen-card"><h3>${world}</h3><p>${unlocked ? `Unlocked - starts at level ${from}` : `Locked - reach level ${from}`}</p></article>`;
  }).join("");  const screens: Record<string, { title: string; html: string }> = {
    menu: {
      title: "Main Menu",
      html: `<article class="screen-card"><h3>Continue Expedition</h3><p>World: ${worlds.find(([from]) => save.progression.campaignLevel >= from)![1]} - Level ${save.progression.campaignLevel}</p><button data-action="play">Play now</button></article>
        <article class="screen-card"><h3>Daily Challenge</h3><p>Everyone receives the same deterministic grid today.</p><button data-action="daily">Enter daily grid</button></article>`,
    },
    worlds: { title: "World Select", html: worldCards },
    levels: {
      title: "Level Select",
      html: `<section class="level-map"><div class="map-path"></div>${Array.from({ length: 20 }, (_, index) => {
        const level = index + 1;
        const id = level <= 5 ? `tutorial-${level}` : `ruins-${level}`;
        const complete = save.progression.completedLevels.includes(id);
        const unlocked = isLevelUnlocked(save, level);
        const stars = save.progression.levelStars[id] ?? 0;
        return `<article class="map-node ${complete ? "complete" : unlocked ? "available" : "locked"}">
          <button ${unlocked ? `data-level="${level}"` : "disabled"}><strong>${level}</strong></button>
          <span>${complete ? `${stars}/3 stars` : unlocked ? "Ready" : "Locked"}</span>
        </article>`;
      }).join("")}</section>`,
    },
    progression: {
      title: "Explorer Progression",
      html: `<div class="progress-list"><div><span>Explorer level</span><b>${explorerLevel()}</b></div><div><span>XP</span><b>${save.progression.xp}</b></div><div><span>Campaign</span><b>${save.progression.campaignLevel}</b></div><div><span>Mastery</span><b>${save.economy.masteryPoints}</b></div><div><span>Relic dust</span><b>${save.economy.relicDust}</b></div></div>`,
    },
    inventory: {
      title: "Inventory & Guild Shop",
      html: `<article class="screen-card">
        <h3>Guild Shop</h3>
        <p>Gold: ${save.economy.gold.toLocaleString()}</p>
        <div class="shop-items">
          <div class="shop-item">
            <span>🧪 Health Potion (+30 HP) - 100 Gold</span>
            <button data-buy="health_potion" data-cost="100">Buy</button>
          </div>
          <div class="shop-item">
            <span>🧭 Dowsing Compass (Reveals 2 tiles) - 150 Gold</span>
            <button data-buy="dowsing_compass" data-cost="150">Buy</button>
          </div>
        </div>
      </article>
      <article class="screen-card"><h3>Lost Relics</h3><p>${save.relicFragments}/5 fragments toward the next relic.</p></article>
      <div class="progress-list">${relics.map(relic =>
        `<div><span>${relic.name}</span><b>${save.unlockedRelics.includes(relic.id) ? "Unlocked" : "Hidden"}</b></div>`
      ).join("")}</div>
      <div class="progress-list">
        <div><span>Health Potion</span><b>x${getItemQuantity("health_potion")}</b></div>
        <div><span>Dowsing Compass</span><b>x${getItemQuantity("dowsing_compass")}</b></div>
      </div>`,
    },
    journal: {
      title: "Explorer Journal",
      html: (() => {
        const journal = explorerJournal(save);
        return `<article class="screen-card journal-summary"><h3>Expedition Record</h3>
          <p>${journal.totalTreasuresCollected} treasures recovered - ${journal.fragments}/${journal.fragmentsRequired} fragments</p></article>
          <div class="relic-journal">${journal.relics.map(relic =>
            `<article class="relic-entry ${relic.unlocked ? "unlocked" : "locked"}">
              <span class="relic-silhouette">${relic.unlocked ? "*" : "?"}</span>
              <div><h3>${relic.unlocked ? relic.name : "Unknown Relic"}</h3>
              <p>${relic.unlocked ? relic.description : "Recover fragments to reveal this relic."}</p></div>
            </article>`).join("")}</div>`;
      })(),
    },
    daily: {
      title: "Daily Challenge",
      html: `<article class="screen-card"><h3>${dateKey()}</h3><p>Seed ${dailyChallengeSeed().toString(16).toUpperCase()} - ${save.daily.completedDates.includes(dateKey()) ? "Completed" : "Not completed"}</p><button data-action="daily">Start challenge</button></article>
        <article class="screen-card"><h3>Daily Tribute</h3><p>${canClaimDailyReward(save.daily, new Date()) ? "Your explorer streak tribute is ready." : "Today's tribute has already been claimed."}</p><button data-action="tribute">Claim tribute</button></article>`,
    },
    achievements: {
      title: "Achievements",
      html: (() => {
        const unlockedCount = achievementCatalog.filter(item => save.achievements.includes(item.id)).length;
        const cards = achievementCatalog.map(item => {
          const unlocked = save.achievements.includes(item.id);
          const unlockedAt = save.achievementUnlockedAt[item.id];
          const meta = unlocked && unlockedAt ? `Unlocked ${new Date(unlockedAt).toLocaleDateString()}` : "Locked";
          return `<article class="screen-card achievement-card ${unlocked ? "unlocked" : "locked"}">
            <h3>${unlocked ? item.name : "???"}</h3>
            <p>${unlocked ? item.description : "Keep exploring to reveal this achievement."}</p>
            <span class="achievement-meta">${meta}</span>
          </article>`;
        }).join("");
        return `<article class="screen-card"><h3>Achievements</h3><p>${unlockedCount}/${achievementCatalog.length} unlocked</p></article>
          <div class="achievement-list">${cards}</div>`;
      })(),
    },
  };
  const screen = screens[name] ?? screens.menu;
  title.textContent = screen.title;
  content.innerHTML = screen.html;
  content.querySelectorAll<HTMLButtonElement>("[data-action]").forEach(button => {
    button.onclick = () => {
      byId<HTMLDialogElement>("screen-dialog").close();
      if (button.dataset.action === "tribute") {
        openDailyReward();
        return;
      }
      dailyMode = button.dataset.action === "daily";
      startRun();
    };
  });
  content.querySelectorAll<HTMLButtonElement>("[data-level]").forEach(button => {
    button.onclick = () => {
      save.progression.campaignLevel = Number(button.dataset.level);
      saveSystem.save(save);
      byId<HTMLDialogElement>("screen-dialog").close();
      startRun();
    };
  });
  content.querySelectorAll<HTMLButtonElement>("[data-buy]").forEach(button => {
    button.onclick = () => {
      const itemId = button.dataset.buy!;
      const cost = Number(button.dataset.cost);
      if (save.economy.gold < cost) {
        alert("Not enough Gold!");
        return;
      }
      save.economy.gold -= cost;
      changeItemQuantity(itemId, 1);
      renderMeta();
      openScreen("inventory");
    };
  });
  byId<HTMLDialogElement>("screen-dialog").showModal();
}

function restartCurrent(): void {
  engine = session.restart(currentSeed);
  document.body.classList.remove("is-paused");
  if (byId<HTMLDialogElement>("pause-dialog").open) byId<HTMLDialogElement>("pause-dialog").close();
  setStatus(tutorialDefinition(save.progression.campaignLevel)?.hint ?? "The expedition has restarted.");
  render();
  startTimer();
}

function formatTime(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function startTimer(): void {
  stopTimer();
  timerHandle = window.setInterval(() => {
    if (!engine || engine.state.status !== "playing") return;
    const result = session.tick();
    render();
    if (result.event === "loss") {
      handleResult(result);
      stopTimer();
    }
  }, 1000);
}

function stopTimer(): void {
  if (timerHandle !== undefined) window.clearInterval(timerHandle);
  timerHandle = undefined;
}

document.querySelectorAll<HTMLButtonElement>("[data-move]").forEach(button => {
  button.onclick = () => move(button.dataset.move as Direction);
});
window.addEventListener("keydown", event => {
  if (document.querySelector("dialog[open]")) return;
  const direction = ({
    ArrowUp: "up", w: "up", W: "up",
    ArrowDown: "down", s: "down", S: "down",
    ArrowLeft: "left", a: "left", A: "left",
    ArrowRight: "right", d: "right", D: "right",
  } as Record<string, Direction>)[event.key];
  if (direction) {
    event.preventDefault();
    move(direction);
  }
});
byId<HTMLButtonElement>("new-run").onclick = startRun;
const itemPotBtn = document.getElementById("use-health-potion") as HTMLButtonElement | null;
if (itemPotBtn) itemPotBtn.onclick = useHealthPotion;
const itemCompBtn = document.getElementById("use-compass") as HTMLButtonElement | null;
if (itemCompBtn) itemCompBtn.onclick = useCompass;
byId<HTMLButtonElement>("pause").onclick = pauseGame;
byId<HTMLButtonElement>("mobile-pause").onclick = pauseGame;
byId<HTMLButtonElement>("resume").onclick = resumeGame;
byId<HTMLButtonElement>("restart-from-pause").onclick = restartCurrent;
byId<HTMLButtonElement>("settings-button").onclick = openSettings;
byId<HTMLButtonElement>("privacy-choices-button").onclick = openPrivacyChoices;
byId<HTMLButtonElement>("back-to-games").onclick = goToGames;
byId<HTMLButtonElement>("replay-tutorial").onclick = () => {
  tutorialActive = true;
  tutorialHasMoved = false;
  if (!engine) startRun(); else render();
};
byId<HTMLButtonElement>("skip-tutorial").onclick = completeTutorial;
byId<HTMLButtonElement>("settings-from-pause").onclick = openSettings;
byId<HTMLButtonElement>("privacy-from-pause").onclick = openPrivacyChoices;
byId<HTMLButtonElement>("replay-tutorial-from-pause").onclick = () => {
  byId<HTMLDialogElement>("pause-dialog").close();
  tutorialActive = true;
  tutorialHasMoved = false;
  if (!engine) startRun(); else render();
};
byId<HTMLButtonElement>("guild-menu-from-pause").onclick = () => {
  byId<HTMLDialogElement>("pause-dialog").close();
  openScreen("menu");
};
byId<HTMLButtonElement>("back-to-games-from-pause").onclick = goToGames;
byId<HTMLButtonElement>("close-settings").onclick = () => {
  byId<HTMLDialogElement>("settings-dialog").close();
  if (engine?.state.status === "paused") byId<HTMLDialogElement>("pause-dialog").showModal();
};
byId<HTMLButtonElement>("save-settings").onclick = saveSettings;
byId<HTMLButtonElement>("reset-save").onclick = resetSave;
document.querySelectorAll<HTMLButtonElement>("[data-screen]").forEach(button => {
  button.onclick = () => openScreen(button.dataset.screen ?? "menu");
});
byId<HTMLButtonElement>("close-screen").onclick = () => {
  byId<HTMLDialogElement>("screen-dialog").close();
  if (engine?.state.status === "paused") byId<HTMLDialogElement>("pause-dialog").showModal();
};
byId<HTMLButtonElement>("close-secret-room").onclick = () => byId<HTMLDialogElement>("secret-room-dialog").close();
byId<HTMLButtonElement>("close-fragment-modal").onclick = () => byId<HTMLDialogElement>("relic-fragment-dialog").close();
byId<HTMLButtonElement>("claim-daily-reward").onclick = claimReward;
byId<HTMLButtonElement>("close-daily-reward").onclick = () => byId<HTMLDialogElement>("daily-reward-dialog").close();
byId<HTMLButtonElement>("close-privacy-choices").onclick = () => {
  byId<HTMLDialogElement>("privacy-choices-dialog").close();
  if (engine?.state.status === "paused") byId<HTMLDialogElement>("pause-dialog").showModal();
};
byId<HTMLButtonElement>("save-privacy-choices").onclick = savePrivacyChoices;
byId<HTMLButtonElement>("help").onclick = () => byId<HTMLDialogElement>("guide").showModal();
byId<HTMLButtonElement>("mobile-guide").onclick = () => byId<HTMLDialogElement>("guide").showModal();
byId<HTMLButtonElement>("close-guide").onclick = byId<HTMLButtonElement>("close-guide-button").onclick = () => byId<HTMLDialogElement>("guide").close();

byId<HTMLButtonElement>("mobile-hamburger").onclick = () => byId<HTMLDialogElement>("mobile-options-dialog").showModal();
byId<HTMLButtonElement>("close-mobile-options").onclick = () => byId<HTMLDialogElement>("mobile-options-dialog").close();
byId<HTMLButtonElement>("mobile-new-run").onclick = () => { byId<HTMLDialogElement>("mobile-options-dialog").close(); startRun(); };
byId<HTMLButtonElement>("mobile-help").onclick = () => { byId<HTMLDialogElement>("mobile-options-dialog").close(); byId<HTMLDialogElement>("guide").showModal(); };
byId<HTMLButtonElement>("mobile-replay-tutorial").onclick = () => {
  byId<HTMLDialogElement>("mobile-options-dialog").close();
  tutorialActive = true;
  tutorialHasMoved = false;
  if (!engine) startRun(); else render();
};
byId<HTMLButtonElement>("mobile-settings").onclick = () => { byId<HTMLDialogElement>("mobile-options-dialog").close(); openSettings(); };
byId<HTMLButtonElement>("mobile-privacy").onclick = () => { byId<HTMLDialogElement>("mobile-options-dialog").close(); openPrivacyChoices(); };
byId<HTMLButtonElement>("mobile-back").onclick = goToGames;
byId<HTMLButtonElement>("continue").onclick = () => {
  byId<HTMLDialogElement>("result").close();
  if (lastRunWon) startRun(); else openMainMenu();
};
byId<HTMLButtonElement>("retry-result").onclick = () => {
  byId<HTMLDialogElement>("result").close();
  restartCurrent();
};
byId<HTMLButtonElement>("share-result").onclick = () => { void shareResult(); };
byId<HTMLButtonElement>("share-facebook").onclick = () => {
  if (!lastShareSummary) return;
  const urls = window.GameShare.socialUrls({ title: "Treasure Expedition", text: lastShareSummary, url: window.location.href });
  window.open(urls.facebook, "_blank", "noopener,noreferrer");
};
byId<HTMLButtonElement>("share-x").onclick = () => {
  if (!lastShareSummary) return;
  const urls = window.GameShare.socialUrls({ title: "Treasure Expedition", text: lastShareSummary, url: window.location.href });
  window.open(urls.x, "_blank", "noopener,noreferrer");
};
byId<HTMLButtonElement>("share-whatsapp").onclick = () => {
  if (!lastShareSummary) return;
  const urls = window.GameShare.socialUrls({ title: "Treasure Expedition", text: lastShareSummary, url: window.location.href });
  window.open(urls.whatsapp, "_blank", "noopener,noreferrer");
};
byId<HTMLButtonElement>("share-linkedin").onclick = () => {
  if (!lastShareSummary) return;
  const urls = window.GameShare.socialUrls({ title: "Treasure Expedition", text: lastShareSummary, url: window.location.href });
  window.open(urls.linkedin, "_blank", "noopener,noreferrer");
};
// Independent of the result's contextual action (Next run/Replay level) -
// always leaves for the games portfolio.
byId<HTMLButtonElement>("result-back-to-games").onclick = goToGames;
byId<HTMLButtonElement>("start-expedition").onclick = () => {
  byId<HTMLDialogElement>("main-menu-dialog").close();
  dailyMode = false;
  startRun();
};
byId<HTMLButtonElement>("continue-expedition").onclick = byId<HTMLButtonElement>("start-expedition").onclick;
byId<HTMLButtonElement>("menu-settings").onclick = () => {
  byId<HTMLDialogElement>("main-menu-dialog").close();
  openSettings();
};

const openLeaderboard = () => {
  if ((window as any).GameLeaderboard) (window as any).GameLeaderboard.open(GAME_ID, "Treasure Expedition");
};
const missionLeaderboard = document.getElementById("mission-leaderboard-button");
if (missionLeaderboard) missionLeaderboard.onclick = openLeaderboard;
const resultLeaderboard = document.getElementById("leaderboard-button");
if (resultLeaderboard) resultLeaderboard.onclick = openLeaderboard;
const mobileLeaderboard = document.getElementById("mobile-leaderboard-button");
if (mobileLeaderboard) mobileLeaderboard.onclick = () => {
  byId<HTMLDialogElement>("mobile-options-dialog").close();
  openLeaderboard();
};

syncAnalyticsConsentControl();
analytics.track("game_opened");
applySettings();
renderMeta();
gridRenderer = new GridRenderer(byId("board"), move, inspect);
installQaHooks();
let swipeStart: { x: number; y: number } | undefined;
const board = byId("board");
board.addEventListener("pointerdown", event => {
  swipeStart = { x: event.clientX, y: event.clientY };
});
board.addEventListener("pointerup", event => {
  if (!swipeStart) return;
  const direction = swipeDirection(swipeStart, { x: event.clientX, y: event.clientY });
  swipeStart = undefined;
  if (direction) {
    event.preventDefault();
    move(direction);
  }
});
board.addEventListener("touchmove", event => event.preventDefault(), { passive: false });
const adBanner = byId("web-ad-banner");
const adFallback = window.setTimeout(() => {
  if (!engine) ads.displayBanner(adBanner);
}, 500);
void ads.initialize().catch(() => false).then(() => {
  window.clearTimeout(adFallback);
  if (!engine) ads.displayBanner(adBanner);
});
void mobile.initialize({
  onBackground: pauseFromLifecycle,
  onForeground: () => undefined,
  onBackButton: handleNativeBack,
});
// The native app IS the whole product on a device - there's no portfolio
// site to return to, so this action only makes sense on web.
if (mobile.native) {
  document.querySelectorAll<HTMLElement>("#back-to-games, #result-back-to-games, #back-to-games-from-pause").forEach(el => {
    el.hidden = true;
  });
}
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    const appBaseUrl = new URL(import.meta.env.BASE_URL, window.location.href);
    void navigator.serviceWorker.register(new URL("sw.js", appBaseUrl).href, { scope: appBaseUrl.pathname });
  });
}
openMainMenu();
